import React, { useState, useRef, useEffect } from 'react';
import Formatter from '../../../utils/Formatter';
import videojs from 'video.js';

const FRAME_STEP = 1 / 25; // ~1 frame at 25fps

const VideoGrid = (props) => {
    const [videoPlayerVisible, setVideoPlayerVisible] = useState(props.preSelectedAsset ? true : false);
    const [currentVideo, setCurrentVideo] = useState(props.preSelectedAsset);
    const [currentTime, setCurrentTime] = useState(props.preSelectedAsset ? props.preSelectedAsset.capturedFrame.timecode : 0);
    const [duration, setDuration] = useState(0);
    const [helpModalVisible, setHelpModalVisible] = useState(false);
    
    // Video.js refs
    const playerRef = useRef(null); // The videojs instance
    const videoNodeRef = useRef(null); // The DOM node
    const canvasRef = useRef(null);

    // Initialize videojs when video player becomes visible
    useEffect(() => {
        if (videoPlayerVisible && currentVideo && videoNodeRef.current) {
            let previewSource = `https://svod.invintus.com/${currentVideo.clientID}/${currentVideo.assetID}/master.m3u8`

            // Create videojs player instance
            playerRef.current = videojs(
                videoNodeRef.current,
                {
                    fill: true,
                    controls: true,
                    autoplay: true,
                    preload: 'auto',
                    controlBar: {
                        volumePanel: {
                            inline: false
                        }
                    },
                    sources: [{
                        src: previewSource,
                        type: 'application/x-mpegURL'
                    }]
                },
                function() {                    
                    this.on('timeupdate', () => {
                        setCurrentTime(this.currentTime());
                    });
                    
                    this.on('loadedmetadata', () => {
                        setDuration(this.duration());
                        if (props.preSelectedAsset && props.preSelectedAsset.capturedFrame) {
                            this.currentTime(props.preSelectedAsset.capturedFrame.timecode);
                            this.pause();
                        }
                    });
                    
                    this.on('error', (e) => {
                        console.error("VideoJS error:", this.error());
                    });
                }
            );
            
            // Return cleanup function
            return () => {
                if (playerRef.current) {
                    playerRef.current.dispose();
                    playerRef.current = null;
                }
            };
        }
    }, [videoPlayerVisible, currentVideo]);

    // Keyboard controls
    useEffect(() => {
        if (!videoPlayerVisible) return;
        const handleKeyDown = (e) => {
            if (!playerRef.current) return;
            let handled = false;

            if (e.key === ' ') {
                if (playerRef.current.paused()) {
                    playerRef.current.play();
                } else {
                    playerRef.current.pause();
                }
                handled = true;
            } else if (e.key === 'ArrowLeft') {
                if (e.shiftKey) {
                    playerRef.current.currentTime(Math.max(0, playerRef.current.currentTime() - FRAME_STEP));
                } else {
                    playerRef.current.currentTime(Math.max(0, playerRef.current.currentTime() - 1));
                }
                handled = true;
            } else if (e.key === 'ArrowRight') {
                if (e.shiftKey) {
                    playerRef.current.currentTime(Math.min(duration, playerRef.current.currentTime() + FRAME_STEP));
                } else {
                    playerRef.current.currentTime(Math.min(duration, playerRef.current.currentTime() + 1));
                }
                handled = true;
            } else if (e.key === 'Enter') {
                handleSelectFrame();
                handled = true;
            }
            if (handled) e.preventDefault();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [videoPlayerVisible, duration]);

    const handlePlayVideo = (asset) => {
        setCurrentVideo(asset);
        setVideoPlayerVisible(true);
    };

    const handleClosePlayer = () => {
        setVideoPlayerVisible(false);
        setCurrentVideo(null);
        setCurrentTime(0);
        setDuration(0);
        setHelpModalVisible(false);
    };

    const handleFrameStep = (dir, frameStep = FRAME_STEP) => {
        if (playerRef.current) {
            let newTime = playerRef.current.currentTime() + dir * frameStep;
            newTime = Math.max(0, Math.min(duration, newTime));
            playerRef.current.currentTime(newTime);
            setCurrentTime(newTime);
            
            // Ensure video is paused after frame steps
            if (!playerRef.current.paused()) {
                playerRef.current.pause();
            }
        }
    };

    const handleShowHelp = () => {
        setHelpModalVisible(true);
    };

    const handleCloseHelp = () => {
        setHelpModalVisible(false);
    };

    // Capture the current frame to canvas and open preview modal
    const handleSelectFrame = () => {
        if (!playerRef.current) return;
        
        // Pause the video to ensure we get the exact frame
        playerRef.current.pause();
        
        try {
            // Get the video element from videojs
            const videoElement = playerRef.current.el().querySelector('video');
            
            // Get video dimensions
            const videoWidth = videoElement.videoWidth || 640;
            const videoHeight = videoElement.videoHeight || 360;
            
            // Create canvas if it doesn't exist
            if (!canvasRef.current) {
                canvasRef.current = document.createElement('canvas');
            }
            
            // Set canvas dimensions
            canvasRef.current.width = videoWidth;
            canvasRef.current.height = videoHeight;
            
            const ctx = canvasRef.current.getContext('2d');
            
            // Clear canvas
            ctx.clearRect(0, 0, videoWidth, videoHeight);
            
            // Try to draw the video frame
            try {
                // Draw video to canvas
                ctx.drawImage(videoElement, 0, 0, videoWidth, videoHeight);
                
                // Try to export the canvas data
                try {
                    const frameDataUrl = canvasRef.current.toDataURL('image/jpeg', 0.95);

                    // Convert data URL to Blob
                    fetch(frameDataUrl)
                        .then(res => res.blob())
                        .then(blob => {
                            // Create a File object from the blob
                            const file = new File([blob], `${currentVideo.assetID}.jpg`, {
                                type: 'image/jpeg'
                            });

                            // Add captured frame info to the video object for parent component
                            const videoWithFrame = {
                                ...currentVideo,
                                capturedFrame: {
                                    file: file,
                                    url: frameDataUrl,
                                    timecode: Formatter.convertSecondsToHMS(currentTime),
                                    exactTime: currentTime
                                }
                            };
                            
                            // Pass to parent's onSelect handler
                            props.onSelect(videoWithFrame, Formatter.convertSecondsToHMS(currentTime));
                            
                            // Close modal and player
                            setVideoPlayerVisible(false);
                        });
                    } catch (exportError) {
                        console.error("Canvas export error:", exportError);
                        setVideoPlayerVisible(false);
                }
            } catch (drawError) {
                console.error("Error drawing to canvas:", drawError);
            }
        } catch (error) {
            console.error("Error in frame capture:", error);
        }
    };


    const formatDuration = (totalRunTime) => {
        if (!totalRunTime) return '00:00';
        try {
            return totalRunTime.split('.')[0];
        } catch (e) {
            return '00:00';
        }
    };

    // If no assets are available, show a message
    if (!props.assets || props.assets.length === 0) {
        return (
            <div className="alert alert-info">
                <p>No video assets available.</p>
            </div>
        );
    }

    return (
        <div className="video-grid">
            {props.assets.map((asset) => (
                <div 
                    key={asset.assetID} 
                    className={`video-card ${props.selectedItem && props.selectedItem.assetID === asset.assetID ? 'selected' : ''}`}
                >
                    <div className="video-thumbnail">
                        {asset.thumbnail ? (
                            <img
                                src={asset.thumbnail}
                                alt={asset.name}
                            />
                        ) : (
                            <div className="video-placeholder" />
                        )}
                        <div
                            className="play-button"
                            onClick={() => handlePlayVideo(asset)}
                        >
                            <i className="fa fa-play-circle"></i>
                        </div>
                    </div>
                    <div className="video-details">
                        <h4 title={asset.name}>{asset.name}</h4>
                        <div className="video-meta">
                            <span className="duration">
                                <i className="fa fa-clock"></i> {formatDuration(asset.totalRunTime)}
                            </span>
                            <span className="asset-status">
                                {asset.currentStatus}
                            </span>
                        </div>
                        <button 
                            className="btn btn-sm btn-primary btn-select" 
                            onClick={() => handlePlayVideo(asset)}
                        >
                            <i className="fa fa-search"></i> Select Frame
                        </button>
                    </div>
                </div>
            ))}
            
            {/* Video Player */}
            {videoPlayerVisible && currentVideo && (
                <div className="video-player-container modal-slim">
                    <div className="video-player modal-slim">
                        <button 
                            className="close-button" 
                            onClick={handleClosePlayer}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                        
                        <div className="video-content">
                            <div className="preview-element" data-vjs-player>
                                <video 
                                    className="video-js vjs-16-9" 
                                    ref={videoNodeRef}
                                    playsInline
                                    crossOrigin="anonymous"
                                />
                            </div>
                            <div className="controls slim-controls">
                                <div className="controls-row">
                                    <div className="frame-controls">
                                        <button className="btn frame-btn" title="Back 10 seconds" onClick={() => handleFrameStep(-1, 10)}>
                                            <i className="fa fa-step-backward"></i>
                                        </button>
                                        <button className="btn frame-btn" title="Forward 10 seconds" onClick={() => handleFrameStep(1, 10)}>
                                            <i className="fa fa-step-forward"></i>
                                        </button>
                                    </div>
                                    <div className="help-section">
                                        <button className="help-link" onClick={handleShowHelp}>
                                            <i className="fa fa-question-circle"></i> Help
                                        </button>
                                    </div>
                                    <div className="action-controls">
                                        <button 
                                            className="btn btn-success use-frame-btn" 
                                            onClick={handleSelectFrame}
                                        >
                                            Use Frame
                                        </button>
                                        <button 
                                            className="btn btn-default cancel-btn" 
                                            onClick={handleClosePlayer}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Help Modal */}
            {helpModalVisible && (
                <div className="help-modal-overlay">
                    <div className="help-modal">
                        <div className="help-modal-header">
                            <h3>Video Frame Selector Help</h3>
                            <button className="close-button" onClick={handleCloseHelp}>
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="help-modal-content">
                            <div className="help-section">
                                <h4><i className="fa fa-keyboard-o"></i> Keyboard Shortcuts</h4>
                                <div className="shortcut-list">
                                    <div className="shortcut-item">
                                        <span className="shortcut-key">←</span>
                                        <span className="shortcut-desc">Jump back 1 second</span>
                                    </div>
                                    <div className="shortcut-item">
                                        <span className="shortcut-key">→</span>
                                        <span className="shortcut-desc">Jump forward 1 second</span>
                                    </div>
                                    <div className="shortcut-item">
                                        <span className="shortcut-key">Shift + ←</span>
                                        <span className="shortcut-desc">Previous frame (~1/25 second)</span>
                                    </div>
                                    <div className="shortcut-item">
                                        <span className="shortcut-key">Shift + →</span>
                                        <span className="shortcut-desc">Next frame (~1/25 second)</span>
                                    </div>
                                    <div className="shortcut-item">
                                        <span className="shortcut-key">Space</span>
                                        <span className="shortcut-desc">Play/Pause</span>
                                    </div>
                                    <div className="shortcut-item">
                                        <span className="shortcut-key">Enter</span>
                                        <span className="shortcut-desc">Use current frame</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="help-section">
                                <h4>How to Use</h4>
                                <ol className="instructions-list">
                                    <li>Use the video controls or timeline to navigate to the desired time</li>
                                    <li>Use the back and forward buttons or keyboard shortcuts for precise positioning</li>
                                    <li>When you find the perfect frame, click "Use Frame" or press Enter</li>
                                    <li>The selected frame will be captured as a thumbnail image</li>
                                </ol>
                            </div>
                            
                            <div className="help-section">
                                <h4><i className="fa fa-lightbulb-o"></i> Tips</h4>
                                <ul className="tips-list">
                                    <li>Pause the video before making precise frame adjustments</li>
                                    <li>Use Shift + Arrow keys for frame-by-frame navigation</li>
                                    <li>You can preview the exact frame before confirming your selection</li>
                                </ul>
                            </div>
                        </div>
                        <div className="help-modal-footer">
                            <button className="btn btn-primary" onClick={handleCloseHelp}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoGrid; 